/**
 * OKS-548 — Frontend types for the creator Activity feed (`/activity`).
 *
 * The feed REUSES the shipped notification wire shape
 * (`NotificationRow` from `@/types/notifications`) verbatim — rows are
 * serialized by `Notifications::Serializer`. The per-type icon + filter
 * chip are derived FE-side from `event_key` (KD-4), so the frozen 5-value
 * serializer `type` contract is untouched.
 */

import type { NotificationRow } from "@/types/notifications"

/**
 * Filter chips (UX Spec §2). `all` is the default. Chip → row mapping is
 * defined by `chipForEventKey` in `activity-feed-row`:
 *   - kudos    → `creator.kudo.received`
 *   - cheers   → `creator.cheer.received`
 *   - missions → `creator.mission.*` + `creator.submission.approved`
 *   - badges   → `creator.badge.earned` (+ rank when it ships — deferred)
 */
export type ActivityChip = "all" | "kudos" | "cheers" | "missions" | "badges"

export const ACTIVITY_CHIPS: readonly ActivityChip[] = [
  "all",
  "kudos",
  "cheers",
  "missions",
  "badges",
] as const

/**
 * Inertia page props for `Creator/Activity/Index`.
 *
 * - `activity_feed` — serialized notification rows (recent window,
 *   newest-first), brand-scoped server-side. OKS-552 — DEFERRED with a
 *   `rescue → nil` (TRD §3). Tri-state the FE branches on:
 *     - `undefined` → not yet resolved → `<Deferred>` loading fallback;
 *     - `null`      → resolved but failed (rescue) → inline error card;
 *     - `[]`        → resolved, no activity → empty state (DISTINCT from null);
 *     - `[...]`     → resolved feed rows.
 * - `today_start_at` — start of the current day in the BRAND timezone as
 *   a UTC ISO8601 instant (KD-5); a row is TODAY iff
 *   `created_at >= today_start_at`. Stays EAGER.
 * - `unread_notifications_count` is read from the shared context
 *   (`useCurrentContext`), NOT a page-local prop.
 */
export interface ActivityIndexProps {
  activity_feed: NotificationRow[] | null
  today_start_at: string
}
